import { forwardRef, type ImgHTMLAttributes, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { default as GenericImage } from 'components/generic/Image';
import { getCloudFlareProxiedUrl } from 'helpers/imageProxyHelper';
import type { TypeImageDetailed } from 'types/autogen/contentful';
import type { Breakpoint } from 'variables';
import { createSrcSet } from './shared/helpers';

const StyledGenericImage = styled(GenericImage)`
    max-width: 100%;
    height: auto;
    width: auto;
`;

type ImageProps = (TypeImageDetailed | undefined) & ImgHTMLAttributes<HTMLImageElement>;

const Image = forwardRef<HTMLImageElement, ImageProps>((props, ref) => {
    const { fields, metadata, sys, ...restProps } = props;
    const { image, altText, localizedImage } = fields;
    const { sizes: propSizes } = restProps;
    const imageAsset = localizedImage || image;

    const desktopBreakpoint: Breakpoint = '1025px';
    //TODO: We need to balance having the smallest default while minimising the amount of times that we have to request a bigger image.
    const [sizes, setSizes] = useState<HTMLImageElement['sizes'] | undefined>(
        propSizes || `(min-width: ${desktopBreakpoint}) 25vw, 50vw`
    );
    const imageRef = useRef<HTMLImageElement | null>(null);
    useImperativeHandle(ref, () => imageRef.current as HTMLImageElement);

    useEffect(() => {
        if (!propSizes) {
            // The image is initially stretched to fill the required space, and its size is checked afterward.
            // If necessary, a higher resolution image will be fetched based on the actual size.
            const imageWidth = imageRef.current?.offsetWidth;
            if (!imageWidth) return;

            setSizes(`${imageWidth}px`);
        }
    }, []);

    const assetFields = imageAsset?.fields;
    const file = assetFields?.file;

    const imageFile = file?.details?.image;
    const url = file?.url;

    const srcSet = useMemo(() => {
        return file ? createSrcSet(file) : undefined;
    }, [file]);

    const cloudFlareProxiedUrl = useMemo(() => {
        return url ? getCloudFlareProxiedUrl(url) : '';
    }, [url]);

    if (!imageAsset) return null;
    if (!assetFields?.file) return null;

    return (
        <StyledGenericImage
            ref={imageRef}
            src={cloudFlareProxiedUrl}
            alt={altText || ''}
            srcSet={srcSet}
            width={imageFile?.width}
            height={imageFile?.height}
            sizes={sizes}
            {...restProps}
        />
    );
});

export default Image;
